import { useCallback, useEffect, useRef, useState } from 'react';
import { profileGateway } from '..';
import type { NotificationPreferences, ProfileResponse } from '../types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

interface FormState {
  firstName: string;
  middleName: string;
  lastName: string;
  displayName: string;
  jobTitle: string;
  preferredLanguage: string;
  timeZone: string;
  mobilePhone: string;
  alternatePhone: string;
  newEmail: string;
  notificationPreferences: NotificationPreferences;
}

function toFormState(profile: ProfileResponse): FormState {
  return {
    firstName: profile.firstName ?? '',
    middleName: profile.middleName ?? '',
    lastName: profile.lastName ?? '',
    displayName: profile.displayName ?? '',
    jobTitle: profile.jobTitle ?? '',
    preferredLanguage: profile.preferredLanguage ?? '',
    timeZone: profile.timeZone ?? '',
    mobilePhone: profile.mobilePhone ?? '',
    alternatePhone: profile.alternatePhone ?? '',
    newEmail: '',
    notificationPreferences: profile.notificationPreferences,
  };
}

function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString();
}

const MAX_FIELD_LENGTH = 100;

/**
 * Real and functional — every field here is independently optional to save (see
 * ProfileGateway.updateProfile's own doc comment). Tenant memberships, company ownership, security roles,
 * subscription plan, payroll permissions, verified-identity status, and MFA status are deliberately never
 * editable here; the Account Information section only ever displays them.
 */
export function MyProfilePage() {
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmTokenInput, setConfirmTokenInput] = useState('');
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const dirty = profile !== null && form !== null && JSON.stringify(toFormState(profile)) !== JSON.stringify(form);

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setLoadError(null);
    try {
      const loaded = await profileGateway.getProfile(signal);
      // React StrictMode's dev-only double-effect means an earlier, already-aborted call's promise can
      // still settle after a fresh one has started — never let a stale/canceled request touch state.
      if (signal?.aborted) return;
      setProfile(loaded);
      setForm(toFormState(loaded));
    } catch (err) {
      if (signal?.aborted) return;
      setLoadError(friendlyErrorMessage(err, 'Could not load your profile.'));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Warn on tab close/navigation away from the browser chrome while there are unsaved edits — an in-app
  // route guard would need a data router (useBlocker), which this app's plain <Routes> tree doesn't use.
  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (dirty) {
        e.preventDefault();
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dirty]);

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setSuccessMessage(null);
  }

  function validate(): boolean {
    if (!form) return false;
    const errors: Record<string, string> = {};
    for (const [key, value] of Object.entries(form)) {
      if (typeof value === 'string' && value.length > MAX_FIELD_LENGTH) {
        errors[key] = `Must be ${MAX_FIELD_LENGTH} characters or fewer.`;
      }
    }
    if (form.newEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.newEmail)) {
      errors.newEmail = 'Enter a valid email address.';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSave() {
    if (!form || !validate()) return;
    setSaving(true);
    setSaveError(null);
    setSuccessMessage(null);
    try {
      const result = await profileGateway.updateProfile({
        firstName: form.firstName,
        middleName: form.middleName,
        lastName: form.lastName,
        displayName: form.displayName,
        jobTitle: form.jobTitle,
        preferredLanguage: form.preferredLanguage,
        timeZone: form.timeZone,
        mobilePhone: form.mobilePhone,
        alternatePhone: form.alternatePhone,
        notificationPreferences: form.notificationPreferences,
        newEmail: form.newEmail || null,
      });
      setProfile(result.profile);
      setForm(toFormState(result.profile));
      setSuccessMessage('Profile saved.');
    } catch (err) {
      setSaveError(friendlyErrorMessage(err, 'Could not save your profile.'));
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    if (!profile) return;
    if (dirty && !window.confirm('Discard unsaved changes?')) {
      return;
    }
    setForm(toFormState(profile));
    setFieldErrors({});
    setSaveError(null);
  }

  async function handleConfirmEmailToken() {
    if (!confirmTokenInput) return;
    setConfirmError(null);
    try {
      await profileGateway.confirmEmailChange(confirmTokenInput);
      const refreshed = await profileGateway.getProfile();
      setProfile(refreshed);
      setForm(toFormState(refreshed));
      setConfirmTokenInput('');
      setSuccessMessage('Email address confirmed.');
    } catch (err) {
      setConfirmError(friendlyErrorMessage(err, 'Could not confirm the email change.'));
    }
  }

  async function handlePhotoSelected(file: File) {
    setPhotoError(null);
    setUploadingPhoto(true);
    try {
      await profileGateway.uploadPhoto(file);
      const refreshed = await profileGateway.getProfile();
      setProfile(refreshed);
    } catch (err) {
      setPhotoError(friendlyErrorMessage(err, 'Could not upload that photo.'));
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  if (loading) {
    return (
      <section className="card card--wide">
        <p className="muted">Loading…</p>
      </section>
    );
  }

  if (loadError || !profile || !form) {
    return (
      <section className="card card--wide">
        <p className="error">{loadError ?? 'Your profile could not be loaded.'}</p>
      </section>
    );
  }

  const photoUrl = profileGateway.getPhotoUrl(profile.hasProfilePhoto);

  return (
    <>
      <section className="card card--wide">
        <h1>My Profile</h1>
        {successMessage && <p className="success">{successMessage}</p>}
        {saveError && <p className="error">{saveError}</p>}

        <h2>Profile identity</h2>
        <div className="inline-form" style={{ alignItems: 'center' }}>
          {photoUrl ? (
            <img src={photoUrl} alt="" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#e5e7eb' }} aria-hidden="true" />
          )}
          <label style={{ cursor: 'pointer', color: 'var(--accent)', textDecoration: 'underline' }}>
            {uploadingPhoto ? 'Uploading…' : 'Change photo'}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              style={{ display: 'none' }}
              disabled={uploadingPhoto}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handlePhotoSelected(file);
              }}
            />
          </label>
        </div>
        {photoError && <p className="error">{photoError}</p>}

        <div className="inline-form" style={{ flexWrap: 'wrap' }}>
          <label>
            First name
            <input value={form.firstName} onChange={(e) => updateField('firstName', e.target.value)} />
            {fieldErrors.firstName && <span className="error">{fieldErrors.firstName}</span>}
          </label>
          <label>
            Middle name
            <input value={form.middleName} onChange={(e) => updateField('middleName', e.target.value)} />
          </label>
          <label>
            Last name
            <input value={form.lastName} onChange={(e) => updateField('lastName', e.target.value)} />
          </label>
          <label>
            Preferred / display name
            <input value={form.displayName} onChange={(e) => updateField('displayName', e.target.value)} />
          </label>
          <label>
            Job title
            <input value={form.jobTitle} onChange={(e) => updateField('jobTitle', e.target.value)} />
          </label>
          <label>
            Preferred language
            <input value={form.preferredLanguage} onChange={(e) => updateField('preferredLanguage', e.target.value)} placeholder="e.g. en-US" />
          </label>
          <label>
            Time zone
            <input value={form.timeZone} onChange={(e) => updateField('timeZone', e.target.value)} placeholder="e.g. America/Los_Angeles" />
          </label>
        </div>

        <h2>Contact information</h2>
        <div className="inline-form" style={{ flexWrap: 'wrap' }}>
          <label>
            Primary email
            <input value={profile.email} disabled />
          </label>
          <label>
            New email (requires verification)
            <input type="email" value={form.newEmail} onChange={(e) => updateField('newEmail', e.target.value)} placeholder="leave blank to keep current email" />
            {fieldErrors.newEmail && <span className="error">{fieldErrors.newEmail}</span>}
          </label>
          <label>
            Mobile phone
            <input value={form.mobilePhone} onChange={(e) => updateField('mobilePhone', e.target.value)} />
          </label>
          <label>
            Alternate phone
            <input value={form.alternatePhone} onChange={(e) => updateField('alternatePhone', e.target.value)} />
          </label>
        </div>
        {profile.pendingEmail && (
          <div className="card" style={{ background: '#f9fafb' }}>
            <p className="muted">Enter the verification code we emailed to {profile.pendingEmail}.</p>
            <div className="inline-form">
              <input
                value={confirmTokenInput}
                onChange={(e) => setConfirmTokenInput(e.target.value)}
                placeholder="Verification code from the email"
                aria-label="Email verification code"
              />
              <button type="button" onClick={() => void handleConfirmEmailToken()}>Confirm email change</button>
            </div>
            {confirmError && <p className="error">{confirmError}</p>}
          </div>
        )}

        <h2>Application preferences</h2>
        <div className="inline-form" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
          <label style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={form.notificationPreferences.emailNotificationsEnabled}
              onChange={(e) => updateField('notificationPreferences', { ...form.notificationPreferences, emailNotificationsEnabled: e.target.checked })}
            />
            Email notifications
          </label>
          <label style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={form.notificationPreferences.productUpdatesEnabled}
              onChange={(e) => updateField('notificationPreferences', { ...form.notificationPreferences, productUpdatesEnabled: e.target.checked })}
            />
            Product update emails
          </label>
        </div>

        <div className="inline-form">
          <button type="button" onClick={() => void handleSave()} disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          <button type="button" className="link" onClick={handleCancel} disabled={saving || !dirty}>
            Cancel
          </button>
          {dirty && <span className="muted">You have unsaved changes.</span>}
        </div>
      </section>

      <section className="card card--wide">
        <h2>Account information</h2>
        <p className="muted">Read-only — see Security Workspace or your company admin for changes to these.</p>
        <p><strong>User ID</strong> {profile.userId}</p>
        <p><strong>Current company</strong> {profile.memberships.find((m) => m.companyId === profile.activeCompanyId)?.companyName ?? 'None'}</p>
        <p><strong>Current role</strong> {profile.memberships.find((m) => m.roleId === profile.activeRoleId)?.roleName ?? 'None'}</p>
        <p><strong>All authorized memberships</strong></p>
        <ul>
          {profile.memberships.map((m) => (
            <li key={m.membershipId}>{m.companyName} — {m.roleName}</li>
          ))}
        </ul>
        <p><strong>Account created</strong> {formatDate(profile.createdAtUtc)}</p>
        <p><strong>Last successful login</strong> {formatDate(profile.lastSuccessfulLoginAtUtc)}</p>
      </section>
    </>
  );
}
